import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useTabActive } from "@/layouts/adminTabs/tabRuntime";
import { Play, Square, Save, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getApiErrorMessage } from "@/lib/api";
import { formatDateTimeSP } from "@/lib/formatDate";
import { formatCnpj } from "@/lib/format";
import { getDfeStatus, type DfeStatus } from "@/lib/adminSupplierInvoicesApi";
import { listEstablishments, type Establishment } from "@/lib/adminEstablishmentsApi";
import { SupplierInvoiceManifestationPanel } from "@/components/admin/SupplierInvoiceManifestationPanel";
import { getManifestationAlerts, type DeadlineAlert } from "@/lib/adminDfeManifestationApi";
import {
  getCollectorConfig,
  getCollectorHistory,
  getCollectorRun,
  getCollectorSchedule,
  runCollector,
  stopCollectorRun,
  updateCollectorConfig,
  type CollectorConfig,
  type CollectorRun,
  type CollectorSchedule,
} from "@/lib/adminDfeCollectorApi";

const STATUS_BADGE: Record<CollectorRun["status"], "default" | "secondary" | "outline" | "destructive"> = {
  RUNNING: "secondary",
  COMPLETED: "default",
  STOPPED: "outline",
  FAILED: "destructive",
};

function nsuGap(s: DfeStatus): string {
  const u = Number(s.ultNsu);
  const m = Number(s.maxNsu);
  if (!Number.isFinite(u) || !Number.isFinite(m)) return "—";
  const d = m - u;
  return d >= 0 ? String(d) : "—";
}

export default function AdminDfeCollector() {
  const [config, setConfig] = useState<CollectorConfig | null>(null);
  const [schedule, setSchedule] = useState<CollectorSchedule | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<DfeStatus | null>(null);
  const [manifestKey, setManifestKey] = useState("");
  const [manifestKeyApplied, setManifestKeyApplied] = useState("");
  const [alerts, setAlerts] = useState<DeadlineAlert[]>([]);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [selectedEstablishment, setSelectedEstablishment] = useState("");
  const [activeRun, setActiveRun] = useState<CollectorRun | null>(null);
  const [history, setHistory] = useState<CollectorRun[]>([]);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tabActive = useTabActive();

  const loadAll = () => {
    getCollectorConfig().then(setConfig).catch((e) => toast.error(getApiErrorMessage(e, "Falha ao carregar a configuração")));
    getCollectorSchedule().then(setSchedule).catch(() => setSchedule(null));
    getManifestationAlerts(15).then(setAlerts).catch(() => setAlerts([]));
    getDfeStatus().then(setStatus).catch(() => setStatus(null));
    getCollectorHistory(30).then((runs) => {
      setHistory(runs);
      // RECUPERABILIDADE: ao abrir/recarregar a tela, reanexa a uma execução ainda em andamento (o progresso é
      // persistido por lote no servidor). Não duplica nem esconde a execução ativa; apenas volta a acompanhá-la.
      const running = runs.find((r) => r.status === "RUNNING");
      if (running) {
        setActiveRun(running);
        startPolling(running.id);
      }
    }).catch(() => setHistory([]));
    listEstablishments().then((l) => {
      const active = l.filter((e) => e.active);
      setEstablishments(active);
      if (active.length === 1) setSelectedEstablishment(active[0].id);
    }).catch(() => setEstablishments([]));
  };

  useEffect(() => {
    loadAll();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Acompanha uma execução ativa (polling do estado); encerra ao finalizar e recarrega o histórico/progresso.
  const startPolling = (runId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      getCollectorRun(runId).then((r) => {
        setActiveRun(r);
        if (r.status !== "RUNNING") {
          if (pollRef.current) clearInterval(pollRef.current);
          getCollectorHistory(30).then(setHistory).catch(() => undefined);
          getDfeStatus().then(setStatus).catch(() => undefined);
        }
      }).catch(() => undefined);
    }, 3000);
  };

  // Aba inativa (keep-alive): pausa o acompanhamento periódico — o progresso da execução é persistido por
  // lote no servidor. Ao reativar, reanexa a uma execução ainda em andamento (sem duplicar: startPolling
  // limpa o intervalo anterior e o ramo inativo o zera).
  useEffect(() => {
    if (!tabActive) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    if (activeRun && activeRun.status === "RUNNING") startPolling(activeRun.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabActive]);

  const saveConfig = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const saved = await updateCollectorConfig({
        enabled: config.enabled,
        intervalMinutes: config.intervalMinutes,
        maxLotes: config.maxLotes,
        maxDurationMinutes: config.maxDurationMinutes,
        lotesCeiling: config.lotesCeiling,
      });
      setConfig(saved);
      toast.success("Configuração da coleta salva.");
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao salvar a configuração"));
    } finally {
      setSaving(false);
    }
  };

  const startExtraordinary = async () => {
    if (!selectedEstablishment) {
      toast.error("Selecione o estabelecimento.");
      return;
    }
    setStarting(true);
    try {
      const { runId } = await runCollector(selectedEstablishment);
      toast.success("Coleta extraordinária iniciada.");
      const r = await getCollectorRun(runId);
      setActiveRun(r);
      startPolling(runId);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao iniciar a coleta"));
    } finally {
      setStarting(false);
    }
  };

  const stopRun = async () => {
    if (!activeRun) return;
    try {
      const r = await stopCollectorRun(activeRun.id);
      setActiveRun(r);
      toast.message("Parada solicitada — não inicia novos lotes (um lote já enviado pode terminar).");
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao solicitar parada"));
    }
  };

  const set = <K extends keyof CollectorConfig>(k: K, v: CollectorConfig[K]) =>
    setConfig((c) => (c ? { ...c, [k]: v } : c));

  return (
    <div className="space-y-6 p-4">
      <div>
        <h1 className="text-2xl font-bold">Buscador de NF-e</h1>
        <p className="text-sm text-muted-foreground">
          Coleta das notas emitidas contra o CNPJ dos estabelecimentos próprios (Distribuição DF-e).
          Exclusivo do ADMIN. A coleta nunca manifesta, confirma internamente, aplica custo ou move estoque/financeiro.
        </p>
      </div>

      {/* Progresso do cursor (NSU) */}
      <div className="rounded-lg border border-border p-4">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-semibold">Progresso do cursor (NSU)</h2>
          <Button size="sm" variant="ghost" onClick={loadAll}><RefreshCw className="h-4 w-4" /> Atualizar</Button>
        </div>
        {status ? (
          <div className="text-sm">
            <span>Último NSU lido: <strong>{status.ultNsu ?? "—"}</strong></span>
            <span className="mx-3">·</span>
            <span>Máximo informado pela SEFAZ: <strong>{status.maxNsu ?? "—"}</strong></span>
            <span className="mx-3">·</span>
            <span>Diferença: <strong>{nsuGap(status)}</strong> posições de NSU</span>
            <p className="mt-1 text-xs text-muted-foreground">
              Cada posição pode ser nota, resumo ou evento — não é a quantidade exata de notas. A coleta avança o
              cursor em lotes; repita/aguarde a rotina até a diferença chegar a zero (fim da fila).
            </p>
          </div>
        ) : <p className="text-sm text-muted-foreground">Sem estado disponível.</p>}
      </div>

      {/* Configuração da coleta agendada */}
      {config && (
        <div className="rounded-lg border border-border p-4">
          <h2 className="mb-3 font-semibold">Coleta automática (por intervalo)</h2>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2">
              <Switch id="col-enabled" checked={config.enabled} onCheckedChange={(v) => set("enabled", v)} />
              <Label htmlFor="col-enabled">Habilitar coleta automática</Label>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="col-interval" className="text-xs">Intervalo (min)</Label>
              <Input id="col-interval" className="w-24" type="number" value={config.intervalMinutes}
                onChange={(e) => set("intervalMinutes", Number(e.target.value))} placeholder="90" />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="col-lotes" className="text-xs">Máx. lotes</Label>
              <Input id="col-lotes" className="w-24" type="number" value={config.maxLotes}
                onChange={(e) => set("maxLotes", Number(e.target.value))} />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="col-dur" className="text-xs">Máx. duração (min)</Label>
              <Input id="col-dur" className="w-24" type="number" value={config.maxDurationMinutes}
                onChange={(e) => set("maxDurationMinutes", Number(e.target.value))} />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="col-ceil" className="text-xs">Teto de lotes</Label>
              <Input id="col-ceil" className="w-24" type="number" value={config.lotesCeiling}
                onChange={(e) => set("lotesCeiling", Number(e.target.value))} />
            </div>
            <Button onClick={() => void saveConfig()} disabled={saving}>
              <Save className="h-4 w-4" /> Salvar
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            A coleta automática só roda com esta habilitação ligada — separada das travas do botão manual. Para no 1º
            limite (lotes/duração) e em erro, incerteza, bloqueio, 137, 656 ou fim de fila. Uma execução por ocorrência
            (intervalo); não inicia outra enquanto houver uma ativa; disparos perdidos não se acumulam.
          </p>
          {schedule && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
              <span>Última execução: <strong>{schedule.lastRunAt ? formatDateTimeSP(schedule.lastRunAt) : "—"}</strong></span>
              <span>Próxima execução: <strong>{schedule.enabled && schedule.nextRunAt ? formatDateTimeSP(schedule.nextRunAt) : "—"}</strong></span>
              {schedule.hasActiveRun && <span className="text-amber-600">Execução ativa em andamento</span>}
              {schedule.lastFinishReason && <span className="text-muted-foreground">Último desfecho: {schedule.lastFinishReason}</span>}
            </div>
          )}
        </div>
      )}

      {/* Coleta extraordinária (manual, ADMIN) */}
      <div className="rounded-lg border border-border p-4">
        <h2 className="mb-3 font-semibold">Coleta extraordinária</h2>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={selectedEstablishment} onValueChange={setSelectedEstablishment}>
            <SelectTrigger className="w-80"><SelectValue placeholder="Selecione o estabelecimento" /></SelectTrigger>
            <SelectContent>
              {establishments.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name} — {formatCnpj(e.cnpj)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => void startExtraordinary()}
            disabled={starting || (activeRun?.status === "RUNNING")}>
            <Play className="h-4 w-4" /> Iniciar
          </Button>
          {activeRun?.status === "RUNNING" && (
            <Button variant="outline" onClick={() => void stopRun()} disabled={activeRun.stopRequested}>
              <Square className="h-4 w-4" /> {activeRun.stopRequested ? "Parando…" : "Parar"}
            </Button>
          )}
        </div>
        {activeRun && (
          <div className="mt-3 rounded border border-border bg-muted/30 p-3 text-sm">
            <div>
              <Badge variant={STATUS_BADGE[activeRun.status]}>{activeRun.status}</Badge>
              <span className="ml-2">lotes: <strong>{activeRun.lotesDone}</strong> · docs: {activeRun.docsTotal} ·
                XML: {activeRun.xmlsTotal} · resumos: {activeRun.resumosTotal} · erros: {activeRun.errorsTotal}</span>
            </div>
            {activeRun.finishReason && <div className="text-xs text-muted-foreground">Motivo: {activeRun.finishReason}</div>}
          </div>
        )}
      </div>

      {/* Alertas de prazo de manifestação conclusiva (D6): só exibição; NUNCA transmite por vencimento. */}
      <div className="rounded-lg border border-border p-4">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-semibold">Alertas de prazo (manifestação conclusiva)</h2>
          <span className="text-xs text-muted-foreground">prazo estimado ≤ 15 dias ou estourado · sem conclusão conhecida</span>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Prazo conclusivo de 90 dias como <strong>ESTIMATIVA</strong> (base emissão; a data de autorização não está
          disponível) — não é vencimento legal e não bloqueia manifestação. Considera resumos DF-e e prévias (SEFAZ
          ou XML importado) e só o que esta aplicação conhece. Nenhuma manifestação é transmitida automaticamente.
        </p>
        {alerts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum prazo conclusivo próximo do vencimento.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fornecedor</TableHead>
                <TableHead>NF nº</TableHead>
                <TableHead className="text-right">Dias p/ prazo (est.)</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.map((a) => (
                <TableRow key={a.accessKey}>
                  <TableCell>{a.emitterName ?? "—"}</TableCell>
                  <TableCell>{a.nNf ?? "—"}</TableCell>
                  <TableCell className="text-right">
                    <span className={a.overdue ? "font-semibold text-destructive" : ""}>
                      {a.overdue ? `estourado (${a.conclusiveDeadlineDays})` : a.conclusiveDeadlineDays}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => { setManifestKey(a.accessKey); setManifestKeyApplied(a.accessKey); }}>
                      Manifestar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Manifestação do destinatário (NT 2020.001) — EXCLUSIVA do ADMIN, aqui no Buscador por chave de acesso.
          Funciona para resumos elegíveis (mesmo sem XML completo) e XML importado manualmente; o backend valida
          destinatário/estabelecimento/ambiente. Separada por completo da Validação Naturexpress. */}
      <div className="rounded-lg border border-border p-4">
        <h2 className="mb-1 font-semibold">Manifestação do destinatário</h2>
        <p className="mb-3 text-xs text-muted-foreground">
          Informe a chave de acesso (44 dígitos) de uma NF elegível para pré-visualizar e manifestar. Elegibilidade
          e ambiente são validados no backend; a coleta nunca manifesta.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Input className="w-[30rem] font-mono text-xs" value={manifestKey} placeholder="Chave de acesso (44 dígitos)"
            onChange={(e) => setManifestKey(e.target.value.replace(/\D/g, "").slice(0, 44))} />
          <Button size="sm" variant="outline" disabled={manifestKey.length !== 44}
            onClick={() => setManifestKeyApplied(manifestKey)}>
            Abrir
          </Button>
        </div>
        {manifestKeyApplied.length === 44 && (
          <div className="mt-3">
            <SupplierInvoiceManifestationPanel accessKey={manifestKeyApplied} />
          </div>
        )}
      </div>

      {/* Histórico */}
      <div className="rounded-lg border border-border p-4">
        <h2 className="mb-3 font-semibold">Histórico de execuções</h2>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Início</TableHead>
              <TableHead>Acionamento</TableHead>
              <TableHead>Situação</TableHead>
              <TableHead className="text-right">Lotes</TableHead>
              <TableHead className="text-right">Docs</TableHead>
              <TableHead className="text-right">XML</TableHead>
              <TableHead className="text-right">Resumos</TableHead>
              <TableHead className="text-right">Erros</TableHead>
              <TableHead>Motivo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground">Nenhuma execução.</TableCell></TableRow>
            )}
            {history.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="whitespace-nowrap">{formatDateTimeSP(r.startedAt)}</TableCell>
                <TableCell>{r.trigger === "AUTO" ? "Automático (rotina)" : "ADMIN"}</TableCell>
                <TableCell><Badge variant={STATUS_BADGE[r.status]}>{r.status}</Badge></TableCell>
                <TableCell className="text-right">{r.lotesDone}</TableCell>
                <TableCell className="text-right">{r.docsTotal}</TableCell>
                <TableCell className="text-right">{r.xmlsTotal}</TableCell>
                <TableCell className="text-right">{r.resumosTotal}</TableCell>
                <TableCell className="text-right">{r.errorsTotal}</TableCell>
                <TableCell className="text-xs">{r.finishReason ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
